import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import { Plan, PlanFormData, planSchema } from '../types';
import { PlanFeatures } from './PlanFeatures';
import { planMutations } from '@/services/plans/mutations';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

interface PlanFormProps {
  providerId: number;
  versionId: number;
  plan?: Plan;
  onSuccess?: () => void;
}

export function PlanForm({
  providerId,
  versionId,
  plan,
  onSuccess
}: PlanFormProps) {
  const queryClient = useQueryClient();
  
  const form = useForm<PlanFormData>({
    resolver: zodResolver(planSchema),
    mode: 'onChange',
    defaultValues: plan ? {
      name: plan.name,
      description: plan.description,
      monthly_price: plan.monthly_price,
      yearly_price: plan.yearly_price,
      color_index: plan.color_index,
      features: plan.features,
      is_addon: plan.is_addon
    } : {
      name: '',
      description: null,
      monthly_price: null,
      yearly_price: null,
      color_index: 0,
      features: [],
      is_addon: null
    
    }
  });

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: (data: PlanFormData) =>
      plan
        ? planMutations.updatePlan(plan.id, data)
        : planMutations.createPlan(providerId, versionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans', providerId, versionId] });
      toast.success(`The plan has been ${plan ? 'updated' : 'created'}.`);
      onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('Plan creation/update error:', error);
      toast.error(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  });

  const onSubmit = async (data: PlanFormData) => {
    console.log('=== FORM SUBMISSION DEBUG ===');
    console.log('Form data being submitted:', data);
    console.log('Form errors:', form.formState.errors);
    console.log('Form is valid:', form.formState.isValid);
    console.log('Provider ID:', providerId, 'Version ID:', versionId);
    
    // Detailed form state debugging
    console.log('Detailed form state:', {
      isValid: form.formState.isValid,
      isSubmitting: form.formState.isSubmitting,
      isDirty: form.formState.isDirty,
      isValidating: form.formState.isValidating,
      submitCount: form.formState.submitCount,
      touchedFields: form.formState.touchedFields,
      dirtyFields: form.formState.dirtyFields,
      defaultValues: form.formState.defaultValues
    });
    
    // Check each field individually
    console.log('Individual field validation:');
    const fields = ['name', 'description', 'monthly_price', 'yearly_price', 'color_index', 'features'];
    fields.forEach(fieldName => {
      const fieldError = form.formState.errors[fieldName as keyof PlanFormData];
      const fieldValue = data[fieldName as keyof PlanFormData];
      console.log(`Field ${fieldName}:`, {
        value: fieldValue,
        error: fieldError,
        touched: form.formState.touchedFields[fieldName as keyof PlanFormData],
        dirty: form.formState.dirtyFields[fieldName as keyof PlanFormData]
      });
    });
    
    // Manual schema validation
    try {
      const validationResult = planSchema.safeParse(data);
      console.log('Manual schema validation result:', validationResult);
      if (!validationResult.success) {
        console.log('Schema validation errors:', validationResult.error.issues);
        validationResult.error.issues.forEach(issue => {
          console.log(`Schema error on ${issue.path.join('.')}: ${issue.message}`);
        });
        console.error('❌ Schema validation failed - blocking submission');
        return;
      } else {
        console.log('✅ Manual schema validation PASSED');
      }
    } catch (error) {
      console.log('Schema validation threw error:', error);
      return;
    }
    
    // Force trigger validation and use its result
    console.log('Triggering manual validation...');
    const isFormValid = await form.trigger();
    console.log('Manual trigger validation result:', isFormValid);
    console.log('Errors after manual trigger:', form.formState.errors);
    
    // Use the trigger result instead of form.formState.isValid
    if (!isFormValid) {
      console.error('❌ Form trigger validation failed - blocking submission');
      console.log('Current form values:', form.getValues());
      return;
    }
    
    console.log('✅ Form validation passed - proceeding with mutation');
    mutate(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Plan Name</FormLabel>
              <FormControl>
                <Input {...field} disabled={isLoading} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  {...field}
                  value={field.value ?? ''}
                  disabled={isLoading}
                  placeholder="Enter plan description"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_addon"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value === true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked === true ? true : null);
                  }}
                  disabled={isLoading}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  Is Add-on
                </FormLabel>
                <p className="text-sm text-muted-foreground">
                  Mark this plan as an add-on service
                </p>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="monthly_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Monthly Price</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    value={field.value?.toString() ?? ''}
                    onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="yearly_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Yearly Price</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    value={field.value?.toString() ?? ''}
                    onChange={(e) => field.onChange(e.target.valueAsNumber || null)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <input type="hidden" {...form.register('color_index')} />

        <PlanFeatures />

        <div className="flex justify-end gap-4">
          <Button
            type="submit"
            disabled={isLoading}
            onClick={() => {
              console.log('Create Plan button clicked');
              console.log('Form state:', {
                isValid: form.formState.isValid,
                isSubmitting: form.formState.isSubmitting,
                errors: form.formState.errors
              });
            }}
          >
            {isLoading ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
          </Button>
        </div>
      </form>
    </Form>
  );
} 